import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ArrowLeft, RefreshCw, Send, Download, Clock, XCircle, IndianRupee, Loader2 } from "lucide-react";

function Action({ onClick, icon: Icon, label, testid, danger }) {
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { await onClick(); } finally { setBusy(false); } };
  return (
    <button onClick={run} disabled={busy} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${danger ? "border-rose-200 text-rose-700 hover:bg-rose-50" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {label}
    </button>
  );
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => toast.error("Failed to load order"));
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const act = (fn, msg) => async () => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const regenerate = act(() => api.post(`/admin/orders/${orderId}/regenerate`), "Regeneration queued");
  const deliverNow = act(() => api.post(`/admin/orders/${orderId}/deliver-now`), "Delivery triggered");
  const resend = act(() => api.post(`/admin/orders/${orderId}/resend-email`), "Email resent");
  const cancel = act(() => api.post(`/admin/orders/${orderId}/cancel`), "Order cancelled");
  const refund = act(() => api.post(`/admin/orders/${orderId}/refund`, { note: "Refund by admin" }), "Marked refunded");
  const reschedule = async () => {
    const when = prompt("New delivery time (ISO, e.g. 2026-01-01T10:00:00Z):");
    if (!when) return;
    try { await api.post(`/admin/orders/${orderId}/reschedule`, { scheduled_delivery_at: when }); toast.success("Rescheduled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const download = async () => {
    try {
      const { data, headers } = await api.get(`/admin/orders/${encodeURIComponent(orderId)}/download`, { responseType: "blob" });
      if (!headers["content-type"]?.includes("application/pdf") || !data.size) {
        throw new Error("Invalid PDF response");
      }
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Aetheria-Numerology-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not download the PDF. Please refresh and try again.");
    }
  };

  if (!order) return <p className="text-slate-400">Loading…</p>;
  const c = order.customer;
  const prof = order.calculation;

  return (
    <div data-testid="admin-order-detail">
      <Link to="/admin/orders" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy mb-5"><ArrowLeft className="w-4 h-4" /> Back to orders</Link>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl text-navy">{order.order_id}</h1>
          <span className="text-sm text-slate-500 capitalize">{order.status.replace(/_/g, " ")}</span>
        </div>
        <span className="text-lg font-semibold text-gold-hover flex items-center"><IndianRupee className="w-4 h-4" />{order.amount}</span>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Action onClick={regenerate} icon={RefreshCw} label="Regenerate" testid="action-regenerate" />
        <Action onClick={deliverNow} icon={Send} label="Deliver now" testid="action-deliver-now" />
        <Action onClick={resend} icon={Send} label="Resend email" testid="action-resend" />
        <Action onClick={reschedule} icon={Clock} label="Reschedule" testid="action-reschedule" />
        {order.pdf_path && <Action onClick={download} icon={Download} label="Download PDF" testid="action-download" />}
        <Action onClick={cancel} icon={XCircle} label="Cancel" testid="action-cancel" danger />
        <Action onClick={refund} icon={IndianRupee} label="Refund" testid="action-refund" danger />
      </div>

      {order.last_error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700 mb-6" data-testid="order-error">
          <strong>Last error:</strong> {order.last_error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Customer details">
          <Kv k="Name" v={c.full_name} />
          <Kv k="Email" v={c.email} />
          <Kv k="Date of birth" v={c.date_of_birth} />
          <Kv k="Birth time" v={c.birth_time || "—"} />
          <Kv k="Birthplace" v={c.birth_place || "—"} />
          <Kv k="Methodology" v={order.methodology} />
          <Kv k="Language" v={c.language} />
          {c.alternate_spelling && <Kv k="Alt spelling" v={c.alternate_spelling} />}
          {c.areas_of_interest && <Kv k="Interests" v={c.areas_of_interest} />}
        </Panel>

        <Panel title="Order & payment">
          <Kv k="Package" v={order.package_name} />
          <Kv k="List price" v={`₹${order.list_price}`} />
          <Kv k="Discount" v={`₹${order.discount || 0}`} />
          <Kv k="Amount paid" v={`₹${order.amount_paid || 0}`} />
          <Kv k="Payment mode" v={order.payment?.mode || "—"} />
          <Kv k="Payment method" v={order.payment?.method || "—"} />
          <Kv k="AI cost (USD)" v={order.ai_cost_usd ? `$${order.ai_cost_usd}` : "—"} />
          <Kv k="Scheduled delivery" v={order.scheduled_delivery_at ? new Date(order.scheduled_delivery_at).toLocaleString() : "—"} />
          <Kv k="Delivered at" v={order.delivered_at ? new Date(order.delivered_at).toLocaleString() : "—"} />
        </Panel>

        {prof && (
          <Panel title="Calculation results">
            {["life_path", "birth_day", "expression", "soul_urge", "personality", "personal_year"].map((key) => (
              <Kv key={key} k={key.replace(/_/g, " ")} v={prof[key]?.result} cap />
            ))}
            {prof.alternate_comparison && (
              <Kv k="Alt name value" v={`${prof.alternate_name.result} (changed: ${String(prof.alternate_comparison.changed)})`} />
            )}
            <p className="text-xs text-slate-400 mt-2">Rule version {prof.rule_version}</p>
          </Panel>
        )}

        <Panel title="Lifecycle history">
          <ol className="space-y-2">
            {(order.status_history || []).map((h, i) => (
              <li key={i} className="flex justify-between text-sm">
                <span className="capitalize text-slate-700">{h.state.replace(/_/g, " ")}</span>
                <span className="text-xs text-slate-400">{new Date(h.at).toLocaleString()}</span>
              </li>
            ))}
          </ol>
        </Panel>

        {order.email_records?.length > 0 && (
          <Panel title="Email records">
            {order.email_records.map((e, i) => (
              <div key={i} className="flex justify-between text-sm border-b border-slate-100 py-1.5 last:border-0">
                <span className="text-slate-700">{e.type} <span className={e.status === "sent" ? "text-emerald-600" : "text-rose-600"}>({e.status})</span></span>
                <span className="text-xs text-slate-400">{new Date(e.at).toLocaleString()}</span>
              </div>
            ))}
          </Panel>
        )}

        {order.report?.sections && (
          <Panel title="Report preview" className="lg:col-span-2">
            <div className="max-h-96 overflow-y-auto space-y-4 pr-2">
              {order.report.sections.map((s, i) => (
                <div key={i}>
                  <h4 className="font-serif text-lg text-navy">{s.heading}</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{s.body}</p>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-6 ${className}`}>
      <h3 className="font-serif text-xl text-navy mb-4">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Kv({ k, v, cap }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-slate-500 shrink-0">{k}</span>
      <span className={`text-slate-900 text-right ${cap ? "capitalize" : ""}`}>{v}</span>
    </div>
  );
}
